(function () {
  'use strict';

  var ELEMENT_NAME = 'mcocod-product-gallery-v2';
  var USER_MEDIA_STATE = new Map();
  if (window.customElements && window.customElements.get(ELEMENT_NAME)) return;

  function afterThemeUpdate(callback) {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(callback);
    });
  }

  function number(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function ProductGalleryV2() {
    return Reflect.construct(HTMLElement, [], ProductGalleryV2);
  }

  ProductGalleryV2.prototype = Object.create(HTMLElement.prototype);
  ProductGalleryV2.prototype.constructor = ProductGalleryV2;
  Object.setPrototypeOf(ProductGalleryV2, HTMLElement);

  ProductGalleryV2.prototype.connectedCallback = function () {
    if (this.initialized) return;
    this.initialized = true;
    this.dataset.galleryReady = 'true';

    this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-thumb]'));
    this.stage = this.querySelector('[data-gallery-stage]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.expandButton = this.querySelector('[data-gallery-expand-button]');
    this.zoomHint = this.querySelector('[data-gallery-zoom-hint]');
    this.zoomHintText = this.querySelector('[data-gallery-zoom-hint-text]');
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.viewer = this.querySelector('[data-gallery-viewer]');
    this.viewerViewport = this.querySelector('[data-gallery-viewer-viewport]');
    this.viewerImage = this.querySelector('[data-gallery-viewer-image]');
    this.viewerCounter = this.querySelector('[data-gallery-viewer-counter]');
    this.zoomResetButton = this.querySelector('[data-gallery-zoom-reset]');
    this.productRoot = this.closest('[data-product-featured]');
    this.productForm = document.getElementById(this.dataset.productFormId || '');
    this.variantSelect = this.productForm && this.productForm.querySelector('select[name="id"]');
    this.lastVariantId = this.variantSelect ? String(this.variantSelect.value || '') : '';
    this.lastVariantMediaId = this.variantSelect && this.variantSelect.selectedOptions.length
      ? String(this.variantSelect.selectedOptions[0].dataset.mdid || '')
      : '';
    this.preserveMediaOnTypeChange = this.dataset.preserveMediaOnTypeChange === 'true';
    this.lastTypeValue = this.getSelectedTypeValue();
    this.hasUserSelectedMedia = false;
    this.userMediaLock = null;
    this.activeMediaId = String(this.dataset.activeMediaId || '');
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.pointers = new Map();
    this.swipeStart = null;
    this.pinchStart = null;
    this.openTrigger = null;
    this.inlineZoomed = false;
    this.viewerMode = 'photo';

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onViewerWheel = this.onViewerWheel.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.onDialogBackdropClick = this.onDialogBackdropClick.bind(this);
    this.onInlinePointerMove = this.onInlinePointerMove.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    if (this.stage) this.stage.addEventListener('pointermove', this.onInlinePointerMove);
    window.addEventListener('resize', this.onViewportChange);
    if (this.variantSelect) this.variantSelect.addEventListener('change', this.onVariantChange);
    if (this.viewerViewport) {
      this.viewerViewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewerViewport.addEventListener('pointermove', this.onPointerMove);
      this.viewerViewport.addEventListener('pointerup', this.onPointerUp);
      this.viewerViewport.addEventListener('pointercancel', this.onPointerUp);
      this.viewerViewport.addEventListener('wheel', this.onViewerWheel, { passive: false });
    }
    if (this.dialog) {
      this.dialog.addEventListener('close', this.onDialogClose);
      this.dialog.addEventListener('click', this.onDialogBackdropClick);
    }

    this.isInitializing = true;
    this.syncFromVariant(false);
    this.isInitializing = false;
    this.restoreUserMedia();
  };

  ProductGalleryV2.prototype.disconnectedCallback = function () {
    if (!this.initialized) return;
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    if (this.stage) this.stage.removeEventListener('pointermove', this.onInlinePointerMove);
    window.removeEventListener('resize', this.onViewportChange);
    if (this.variantSelect) this.variantSelect.removeEventListener('change', this.onVariantChange);
    if (this.viewerViewport) {
      this.viewerViewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewerViewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewerViewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewerViewport.removeEventListener('pointercancel', this.onPointerUp);
      this.viewerViewport.removeEventListener('wheel', this.onViewerWheel);
    }
    if (this.dialog) {
      this.dialog.removeEventListener('close', this.onDialogClose);
      this.dialog.removeEventListener('click', this.onDialogBackdropClick);
    }
    this.exitInlineZoom();
    this.initialized = false;
  };

  ProductGalleryV2.prototype.isAvailable = function (element) {
    return element && !element.classList.contains('is--media-hide');
  };

  ProductGalleryV2.prototype.getAvailableSlides = function () {
    return this.slides.filter(this.isAvailable.bind(this));
  };

  ProductGalleryV2.prototype.getAvailableThumbs = function () {
    return this.thumbs.filter(this.isAvailable.bind(this));
  };

  ProductGalleryV2.prototype.getSlide = function (mediaId) {
    mediaId = String(mediaId || '');
    return this.slides.find(function (slide) {
      return slide.dataset.galleryMediaId === mediaId;
    });
  };

  ProductGalleryV2.prototype.getActiveSlide = function () {
    return this.getSlide(this.activeMediaId);
  };

  ProductGalleryV2.prototype.getCurrentVariantId = function () {
    return new URL(window.location.href).searchParams.get('variant') || (this.variantSelect ? this.variantSelect.value : '');
  };

  ProductGalleryV2.prototype.getSelectedTypeValue = function () {
    var selector = this.productForm && this.productForm.querySelector('custom-selector[data-option-key="type"] .selector-item.is-selected');
    return selector ? String(selector.getAttribute('value') || '') : '';
  };

  ProductGalleryV2.prototype.restoreUserMedia = function () {
    var stateKey = String(this.dataset.productId || this.id || '');
    var saved = stateKey && USER_MEDIA_STATE.get(stateKey);
    if (!saved) return;
    if (String(saved.variantId || '') !== String(this.getCurrentVariantId() || '')) {
      USER_MEDIA_STATE.delete(stateKey);
      return;
    }
    var savedSlide = this.getSlide(saved.mediaId);
    if (!savedSlide || !this.isAvailable(savedSlide)) {
      USER_MEDIA_STATE.delete(stateKey);
      return;
    }
    this.hasUserSelectedMedia = true;
    this.setActive(saved.mediaId, { userInitiated: false, focusThumb: false });
  };

  ProductGalleryV2.prototype.onVariantChange = function () {
    var nextVariantId = this.variantSelect ? String(this.variantSelect.value || '') : '';
    var nextTypeValue = this.getSelectedTypeValue();
    var typeChanged = this.preserveMediaOnTypeChange && nextTypeValue !== this.lastTypeValue;
    this.lastTypeValue = nextTypeValue;
    if (!nextVariantId || nextVariantId === this.lastVariantId) {
      this.syncGroupingState();
      return;
    }
    this.lastVariantId = nextVariantId;
    if (typeChanged) {
      this.syncGroupingState();
      return;
    }
    var gallery = this;
    afterThemeUpdate(function () {
      gallery.syncFromVariant(true);
    });
  };

  ProductGalleryV2.prototype.syncFromVariant = function (preserveCurrentWhenNoMedia) {
    this.syncGroupingState();
    var mediaId = '';
    if (this.variantSelect && this.variantSelect.selectedOptions.length) {
      mediaId = this.variantSelect.selectedOptions[0].dataset.mdid || '';
    }
    var currentSlide = this.getActiveSlide();
    if (
      preserveCurrentWhenNoMedia &&
      this.hasUserSelectedMedia &&
      currentSlide &&
      currentSlide.dataset.variantMedia !== 'true'
    ) {
      return;
    }

    var desired = mediaId && this.getSlide(mediaId);
    if (!desired || !this.isAvailable(desired)) {
      desired = preserveCurrentWhenNoMedia ? this.getSlide(this.activeMediaId) : null;
    }
    if (!desired || !this.isAvailable(desired)) desired = this.getAvailableSlides()[0];
    if (desired) {
      if (mediaId) this.lastVariantMediaId = String(mediaId);
      this.hasUserSelectedMedia = false;
      this.setActive(desired.dataset.galleryMediaId, { userInitiated: false, focusThumb: false });
    }
  };

  ProductGalleryV2.prototype.syncGroupingState = function () {
    var gallery = this;
    this.thumbs.forEach(function (thumb) {
      var slide = gallery.getSlide(thumb.dataset.galleryMediaId);
      thumb.classList.toggle('is--media-hide', !slide || slide.classList.contains('is--media-hide'));
    });
  };

  ProductGalleryV2.prototype.reassertUserMedia = function (mediaId) {
    var gallery = this;
    afterThemeUpdate(function () {
      var lock = gallery.userMediaLock;
      if (!lock || lock.expiresAt <= Date.now() || lock.mediaId !== String(mediaId)) return;
      if (gallery.activeMediaId !== lock.mediaId) {
        gallery.setActive(lock.mediaId, { userInitiated: false, focusThumb: false });
      }
    });
  };

  ProductGalleryV2.prototype.setActive = function (mediaId, options) {
    options = options || {};
    var previousActiveMediaId = String(this.activeMediaId || '');
    var previousActiveSlide = this.getSlide(previousActiveMediaId);
    var target = this.getSlide(mediaId);
    if (!target || !this.isAvailable(target)) target = this.getAvailableSlides()[0];
    if (!target) return;
    if (options.userInitiated !== true && target.dataset.variantMedia === 'true') {
      var targetVariantMediaId = String(target.dataset.galleryMediaId || '');
      var lockIsActive = this.userMediaLock && this.userMediaLock.expiresAt > Date.now();
      if (lockIsActive && targetVariantMediaId === this.userMediaLock.variantMediaId) return;
      if (targetVariantMediaId === this.lastVariantMediaId && this.hasUserSelectedMedia) return;
      this.lastVariantMediaId = targetVariantMediaId;
      this.hasUserSelectedMedia = false;
      this.userMediaLock = null;
      if (!this.isInitializing) USER_MEDIA_STATE.delete(String(this.dataset.productId || this.id || ''));
    }

    this.pauseInactiveMedia(target);
    this.exitInlineZoom();
    this.activeMediaId = String(target.dataset.galleryMediaId);
    this.dataset.activeMediaId = this.activeMediaId;

    this.slides.forEach(function (slide) {
      var active = slide === target;
      slide.hidden = !active;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('data-gallery-active', active);
    });

    var activeThumb = null;
    this.thumbs.forEach(function (thumb) {
      var active = thumb.dataset.galleryMediaId === String(target.dataset.galleryMediaId);
      thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      thumb.tabIndex = active ? 0 : -1;
      if (active) activeThumb = thumb;
    });

    this.updateCounter();
    this.updateStageControls(target);
    this.prefetchAdjacent();

    if (activeThumb) {
      var thumbRail = activeThumb.closest('[data-gallery-thumbs]');
      if (thumbRail) {
        var horizontalRail = thumbRail.scrollWidth > thumbRail.clientWidth;
        var thumbOffset = horizontalRail
          ? activeThumb.offsetLeft - (thumbRail.clientWidth - activeThumb.offsetWidth) / 2
          : activeThumb.offsetTop - (thumbRail.clientHeight - activeThumb.offsetHeight) / 2;
        thumbRail.scrollTo(horizontalRail
          ? { left: Math.max(0, thumbOffset), behavior: options.focusThumb ? 'smooth' : 'auto' }
          : { top: Math.max(0, thumbOffset), behavior: options.focusThumb ? 'smooth' : 'auto' });
      }
      if (options.focusThumb) activeThumb.focus({ preventScroll: true });
    }
    if (options.userInitiated) {
      this.hasUserSelectedMedia = true;
      if (target.dataset.variantMedia === 'true') {
        this.userMediaLock = null;
        USER_MEDIA_STATE.delete(String(this.dataset.productId || this.id || ''));
      } else {
        USER_MEDIA_STATE.set(String(this.dataset.productId || this.id || ''), {
          mediaId: this.activeMediaId,
          variantId: this.getCurrentVariantId()
        });
        this.userMediaLock = {
          mediaId: this.activeMediaId,
          variantMediaId: this.lastVariantMediaId || (
            previousActiveSlide && previousActiveSlide.dataset.variantMedia === 'true'
              ? previousActiveMediaId
              : ''
          ),
          expiresAt: Date.now() + 2500
        };
        this.reassertUserMedia(this.activeMediaId);
      }
      this.maybeChangeVariant(this.activeMediaId);
    }
    if (this.dialog && this.dialog.open) this.renderViewer();
  };

  ProductGalleryV2.prototype.maybeChangeVariant = function (mediaId) {
    if (this.dataset.changeVariantByImage !== 'true' || !this.variantSelect) return;
    var slide = this.getSlide(mediaId);
    if (!slide || slide.dataset.variantMedia !== 'true') return;

    var option = Array.prototype.find.call(this.variantSelect.options, function (candidate) {
      return !candidate.disabled && candidate.dataset.mdid === String(mediaId);
    });
    var currentVariantId = new URL(window.location.href).searchParams.get('variant') || this.variantSelect.value;
    if (!option || option.value === currentVariantId) return;

    var picker = document.querySelector('super-variant-picker');
    var variantOptions = [];
    if (picker && Array.isArray(picker.variantMainData)) {
      var matchedVariant = picker.variantMainData.find(function (variant) {
        return String(variant.id) === String(option.value);
      });
      if (matchedVariant && Array.isArray(matchedVariant.options)) variantOptions = matchedVariant.options;
    }
    if (!variantOptions.length) {
      variantOptions = String(option.textContent || '').split(' / ').map(function (value) {
        return value.trim().replace(/"/g, '');
      });
    }

    var usedProductSelector = false;
    document.querySelectorAll('custom-selector[option-type="main-product"]').forEach(function (selector) {
      var desiredItem = Array.prototype.find.call(selector.querySelectorAll('.selector-item'), function (item) {
        return variantOptions.indexOf(String(item.getAttribute('value') || '').replace(/"/g, '')) !== -1;
      });
      if (!desiredItem) return;
      usedProductSelector = true;
      if (!desiredItem.classList.contains('is-selected')) desiredItem.click();
    });

    this.variantSelect.value = option.value;
    this.lastVariantId = String(option.value);
    if (!usedProductSelector) {
      this.variantSelect.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
    }
  };

  ProductGalleryV2.prototype.pauseInactiveMedia = function (activeSlide) {
    this.slides.forEach(function (slide) {
      if (slide === activeSlide) return;
      slide.querySelectorAll('video').forEach(function (video) { video.pause(); });
      slide.dispatchEvent(new CustomEvent('mediaHidden', { bubbles: true }));
    });
    activeSlide.dispatchEvent(new CustomEvent('mediaVisible', { bubbles: true }));
  };

  ProductGalleryV2.prototype.updateCounter = function () {
    var available = this.getAvailableSlides();
    var index = available.findIndex(function (slide) {
      return slide.dataset.galleryMediaId === this.activeMediaId;
    }, this);
    var value = (index + 1) + ' / ' + available.length;
    if (this.counter) this.counter.textContent = value;
    if (this.viewerCounter) this.viewerCounter.textContent = value;
  };

  ProductGalleryV2.prototype.updateStageControls = function (slide) {
    var isImage = slide.dataset.mediaType === 'image';
    var isDocument = slide.dataset.document === 'true';
    var compact = this.isCompactViewport();
    var imageButton = slide.querySelector('[data-gallery-image-button]');
    if (this.expandButton) this.expandButton.hidden = !isImage;
    if (imageButton) {
      imageButton.setAttribute('aria-label', isDocument
        ? this.dataset.labelOpenDocument
        : (compact ? this.dataset.labelOpenFullscreen : this.dataset.labelZoomImage));
      imageButton.setAttribute('aria-pressed', this.inlineZoomed ? 'true' : 'false');
    }
    if (this.zoomHint) this.zoomHint.hidden = !isImage || this.inlineZoomed;
    if (this.zoomHintText && isImage) {
      this.zoomHintText.textContent = compact
        ? this.dataset.labelTapFullscreen
        : (isDocument ? this.dataset.labelOpenDocument : this.dataset.labelClickInspect);
    }
  };

  ProductGalleryV2.prototype.isCompactViewport = function () {
    return window.matchMedia('(max-width: 900px)').matches;
  };

  ProductGalleryV2.prototype.onViewportChange = function () {
    if (this.isCompactViewport()) this.exitInlineZoom();
    var slide = this.getActiveSlide();
    if (slide) this.updateStageControls(slide);
  };

  ProductGalleryV2.prototype.navigate = function (direction, userInitiated) {
    var available = this.getAvailableSlides();
    if (available.length < 2) return;
    var current = available.findIndex(function (slide) {
      return slide.dataset.galleryMediaId === this.activeMediaId;
    }, this);
    if (current < 0) current = 0;
    var target = available[(current + direction + available.length) % available.length];
    this.setActive(target.dataset.galleryMediaId, { userInitiated: userInitiated !== false, focusThumb: false });
  };

  ProductGalleryV2.prototype.scrollThumbs = function (direction) {
    var rail = this.querySelector('[data-gallery-thumbs]');
    if (!rail) return;
    var horizontal = rail.scrollWidth > rail.clientWidth;
    rail.scrollBy(horizontal ? { left: direction * 260, behavior: 'smooth' } : { top: direction * 260, behavior: 'smooth' });
  };

  ProductGalleryV2.prototype.exitInlineZoom = function () {
    this.inlineZoomed = false;
    if (this.stage) this.stage.classList.remove('is-inline-zoomed');
    this.querySelectorAll('[data-gallery-image-button]').forEach(function (button) {
      button.classList.remove('is-inline-zoomed');
      button.setAttribute('aria-pressed', 'false');
      var image = button.querySelector('.mcocod-gallery-v2__image');
      if (image) image.style.transformOrigin = '50% 50%';
    });
  };

  ProductGalleryV2.prototype.toggleInlineZoom = function (event, imageButton) {
    var slide = imageButton && imageButton.closest('[data-gallery-slide]');
    if (!slide) return;
    if (this.isCompactViewport() || slide.dataset.document === 'true') {
      this.openViewer(imageButton);
      return;
    }

    this.inlineZoomed = !this.inlineZoomed;
    imageButton.classList.toggle('is-inline-zoomed', this.inlineZoomed);
    imageButton.setAttribute('aria-pressed', this.inlineZoomed ? 'true' : 'false');
    if (this.stage) this.stage.classList.toggle('is-inline-zoomed', this.inlineZoomed);
    if (this.zoomHint) this.zoomHint.hidden = this.inlineZoomed;
    if (this.inlineZoomed) this.updateInlineOrigin(event);
    else {
      var image = imageButton.querySelector('.mcocod-gallery-v2__image');
      if (image) image.style.transformOrigin = '50% 50%';
      this.updateStageControls(slide);
    }
  };

  ProductGalleryV2.prototype.updateInlineOrigin = function (event) {
    if (!this.inlineZoomed || !this.stage) return;
    var slide = this.getActiveSlide();
    var image = slide && slide.querySelector('.mcocod-gallery-v2__image');
    if (!image) return;
    var rect = this.stage.getBoundingClientRect();
    var hasPointerPosition = event && event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    var x = hasPointerPosition ? ((event.clientX - rect.left) / rect.width) * 100 : 50;
    var y = hasPointerPosition ? ((event.clientY - rect.top) / rect.height) * 100 : 50;
    image.style.transformOrigin = Math.max(0, Math.min(100, x)) + '% ' + Math.max(0, Math.min(100, y)) + '%';
  };

  ProductGalleryV2.prototype.onInlinePointerMove = function (event) {
    this.updateInlineOrigin(event);
  };

  ProductGalleryV2.prototype.onClick = function (event) {
    var control = event.target.closest('[data-gallery-thumb], [data-gallery-previous], [data-gallery-next], [data-gallery-rail-previous], [data-gallery-rail-next], [data-gallery-viewer-previous], [data-gallery-viewer-next], [data-gallery-close], [data-gallery-zoom-in], [data-gallery-zoom-out], [data-gallery-zoom-reset], [data-gallery-load-media], [data-gallery-image-button], [data-gallery-expand-button]');
    if (!control || !this.contains(control)) return;
    event.stopPropagation();

    if (control.matches('[data-gallery-thumb]')) {
      var thumb = control;
      this.setActive(thumb.dataset.galleryMediaId, { userInitiated: true, focusThumb: false });
      return;
    }
    if (control.matches('[data-gallery-previous]')) return this.navigate(-1, true);
    if (control.matches('[data-gallery-next]')) return this.navigate(1, true);
    if (control.matches('[data-gallery-rail-previous]')) return this.scrollThumbs(-1);
    if (control.matches('[data-gallery-rail-next]')) return this.scrollThumbs(1);
    if (control.matches('[data-gallery-viewer-previous]')) return this.navigate(-1, true);
    if (control.matches('[data-gallery-viewer-next]')) return this.navigate(1, true);
    if (control.matches('[data-gallery-close]')) return this.closeViewer();
    if (control.matches('[data-gallery-zoom-in]')) return this.setZoom(this.zoom + .5);
    if (control.matches('[data-gallery-zoom-out]')) return this.setZoom(this.zoom - .5);
    if (control.matches('[data-gallery-zoom-reset]')) return this.resetZoom();

    if (control.matches('[data-gallery-load-media]')) return this.loadDeferredMedia(control.closest('[data-gallery-slide]'));

    if (control.matches('[data-gallery-image-button]')) return this.toggleInlineZoom(event, control);
    if (control.matches('[data-gallery-expand-button]')) return this.openViewer(control);
  };

  ProductGalleryV2.prototype.onKeydown = function (event) {
    if (event.key === 'Escape' && this.dialog && this.dialog.open) {
      event.preventDefault();
      this.closeViewer();
      return;
    }
    if (event.key === 'Escape' && this.inlineZoomed) {
      event.preventDefault();
      this.exitInlineZoom();
      var activeSlide = this.getActiveSlide();
      if (activeSlide) this.updateStageControls(activeSlide);
      return;
    }

    var thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) {
      var available = this.getAvailableThumbs();
      var index = available.indexOf(thumb);
      var target = null;
      if (event.key === 'Home') target = available[0];
      if (event.key === 'End') target = available[available.length - 1];
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = available[(index + 1) % available.length];
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = available[(index - 1 + available.length) % available.length];
      if (target) {
        event.preventDefault();
        this.setActive(target.dataset.galleryMediaId, { userInitiated: true, focusThumb: true });
      }
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigate(-1, true);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigate(1, true);
    }
  };

  ProductGalleryV2.prototype.loadDeferredMedia = function (slide) {
    if (!slide || slide.dataset.loaded === 'true') return;
    var template = slide.querySelector('[data-gallery-media-template]');
    var button = slide.querySelector('[data-gallery-load-media]');
    if (!template) return;
    slide.appendChild(template.content.cloneNode(true));
    slide.dataset.loaded = 'true';
    if (button) button.hidden = true;
    var video = slide.querySelector('video');
    if (video) video.play().catch(function () {});
    slide.dispatchEvent(new CustomEvent('mediaVisible', { bubbles: true }));
  };

  ProductGalleryV2.prototype.prefetchAdjacent = function () {
    var available = this.getAvailableSlides();
    var index = available.findIndex(function (slide) { return slide.dataset.galleryMediaId === this.activeMediaId; }, this);
    if (index < 0) return;
    [-1, 1].forEach(function (offset) {
      var slide = available[(index + offset + available.length) % available.length];
      var image = slide && slide.querySelector('img[loading="lazy"]');
      if (image) image.loading = 'eager';
    });
  };

  ProductGalleryV2.prototype.openViewer = function (trigger) {
    var slide = this.getActiveSlide();
    if (!slide || slide.dataset.mediaType !== 'image' || !this.dialog) return;
    this.openTrigger = trigger || this.expandButton;
    this.renderViewer();
    document.body.classList.add('mcocod-gallery-v2-open');
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    var close = this.querySelector('[data-gallery-close]');
    if (close) close.focus({ preventScroll: true });
  };

  ProductGalleryV2.prototype.closeViewer = function () {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
    else {
      this.dialog.removeAttribute('open');
      this.onDialogClose();
    }
  };

  ProductGalleryV2.prototype.onDialogClose = function () {
    document.body.classList.remove('mcocod-gallery-v2-open');
    this.resetZoom();
    if (this.openTrigger) this.openTrigger.focus({ preventScroll: true });
  };

  ProductGalleryV2.prototype.onDialogBackdropClick = function (event) {
    if (event.target === this.dialog) this.closeViewer();
  };

  ProductGalleryV2.prototype.renderViewer = function () {
    var slide = this.getActiveSlide();
    if (!slide || !this.viewerImage) return;
    var image = slide.querySelector('.mcocod-gallery-v2__image');
    if (!image) return;
    var isDocument = slide.dataset.document === 'true';
    this.viewerMode = isDocument ? 'document' : 'photo';
    this.viewer.dataset.viewerMode = this.viewerMode;
    this.viewer.classList.toggle('is-document', isDocument);
    this.viewerImage.alt = image.alt || '';
    var imageButton = slide.querySelector('[data-gallery-image-button]');
    this.viewerImage.src = (imageButton && imageButton.dataset.master) || image.currentSrc || image.src;
    this.resetZoom();
    this.updateCounter();
  };

  ProductGalleryV2.prototype.setZoom = function (value, anchorX, anchorY) {
    var previous = this.zoom;
    this.zoom = Math.max(1, Math.min(4, number(value, 1)));
    if (this.zoom === 1) {
      this.panX = 0;
      this.panY = 0;
    } else if (previous !== this.zoom && Number.isFinite(anchorX) && Number.isFinite(anchorY)) {
      var factor = this.zoom / previous;
      this.panX = anchorX - (anchorX - this.panX) * factor;
      this.panY = anchorY - (anchorY - this.panY) * factor;
    }
    this.renderZoom();
  };

  ProductGalleryV2.prototype.resetZoom = function () {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.renderZoom();
  };

  ProductGalleryV2.prototype.renderZoom = function () {
    if (!this.viewerImage || !this.viewer) return;
    if (this.viewer.classList.contains('is-document')) {
      this.viewerImage.style.transform = 'none';
      this.viewerImage.style.width = this.zoom === 1 ? '100%' : (this.zoom * 100) + '%';
      this.viewerImage.style.maxWidth = this.zoom === 1 ? '1120px' : 'none';
    } else {
      this.viewerImage.style.width = '';
      this.viewerImage.style.maxWidth = '';
      this.viewerImage.style.transform = 'translate3d(' + this.panX + 'px,' + this.panY + 'px,0) scale(' + this.zoom + ')';
    }
    if (this.zoomResetButton) this.zoomResetButton.textContent = (this.zoom % 1 ? this.zoom.toFixed(1) : this.zoom) + '×';
  };

  ProductGalleryV2.prototype.onViewerWheel = function (event) {
    if (!this.dialog || !this.dialog.open || this.viewer.classList.contains('is-document')) return;
    event.preventDefault();
    var rect = this.viewerViewport.getBoundingClientRect();
    this.setZoom(this.zoom + (event.deltaY < 0 ? .35 : -.35), event.clientX - rect.left - rect.width / 2, event.clientY - rect.top - rect.height / 2);
  };

  ProductGalleryV2.prototype.onPointerDown = function (event) {
    if (!this.dialog || !this.dialog.open || this.viewer.classList.contains('is-document')) return;
    this.viewerViewport.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 1) {
      this.swipeStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
      this.dragStart = { x: event.clientX, y: event.clientY, panX: this.panX, panY: this.panY };
    }
    if (this.pointers.size === 2) {
      var points = Array.from(this.pointers.values());
      this.swipeStart = null;
      this.pinchStart = {
        distance: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y),
        zoom: this.zoom
      };
    }
  };

  ProductGalleryV2.prototype.onPointerMove = function (event) {
    if (!this.pointers.has(event.pointerId)) return;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 2 && this.pinchStart) {
      var points = Array.from(this.pointers.values());
      var distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      this.setZoom(this.pinchStart.zoom * distance / Math.max(1, this.pinchStart.distance));
    } else if (this.zoom > 1 && this.dragStart) {
      this.panX = this.dragStart.panX + event.clientX - this.dragStart.x;
      this.panY = this.dragStart.panY + event.clientY - this.dragStart.y;
      this.renderZoom();
    }
  };

  ProductGalleryV2.prototype.onPointerUp = function (event) {
    if (!this.pointers.has(event.pointerId)) return;
    var swipe = this.pointers.size === 1 && this.zoom === 1 && this.swipeStart && this.swipeStart.id === event.pointerId;
    var deltaX = swipe ? event.clientX - this.swipeStart.x : 0;
    var deltaY = swipe ? event.clientY - this.swipeStart.y : 0;
    this.pointers.delete(event.pointerId);
    if (this.pointers.size < 2) this.pinchStart = null;
    if (!this.pointers.size) {
      this.dragStart = null;
      this.swipeStart = null;
      if (Math.abs(deltaX) > 55 && Math.abs(deltaX) > Math.abs(deltaY)) this.navigate(deltaX > 0 ? -1 : 1, true);
    }
  };

  if (window.customElements) window.customElements.define(ELEMENT_NAME, ProductGalleryV2);
})();
